import { SERVER_TIME_OFFSET } from "./restUtil";
import { getInstructorBillingType, InstructorBillingType } from "./sessionStatus";
import { isAssessmentSession } from "./sessions";
import { TagCategory } from "../model/tag";
import { ExerciseTypes } from "./exercises";
import moment from "moment";
import * as _ from "underscore";
import { DEFAULT_ROOT_NAME } from "../pages/Library/LibraryFormaters";
import { ErrorKeys } from "./errorKeys";
import { ReactComponent as ErrorIcon } from "images/errorIcon.svg";
import { HStack } from "components/Stack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import React from "react";
import { Trans } from "react-i18next";

// 5 mins.
export const VALID_PERIOD_OUTSIDE_LESSON = 5;
// 30 mins.
const BEFORE_LESSON_COUNTING_DOWN_PERIOD = 30;
// Hour in mins.
export const LESSON_DURATION = 60;
// 5 mins before official end or 55 mins after official start (40 mins for assessment).
const VALID_FOR_POST_LESSON_FEEDBACK_PERIOD = 55;
const VALID_FOR_POST_ASSESSMENT_FEEDBACK_PERIOD = 40;

export class LocationType {
  static ADDRESS = "address";
  static CITIES = "(cities)";
}
export const SCHOOL_LOCATION_TYPE = LocationType.ADDRESS;

export function isSmallWindow() {
  return window.innerWidth < 850;
}

export const EMAIL_REGEX =
  // eslint-disable-next-line
  /^(([^<>()\[\]\.,;:\s@"]+(\.[^<>()\[\]\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/;

export const CHROME_DOWNLOAD_URL =
  "https://www.google.com/chrome/browser/desktop/index.html";
export class CommunicationPlatform {
  static TALAERA = "Talaera";
  static SKYPE = "Skype";
  static HANGOUTS = "Hangouts";
  static ZOOM = "Zoom";
  static ZOOM_WEB = "ZoomVideo";

  static getAll = () => {
    return [
      CommunicationPlatform.TALAERA,
      CommunicationPlatform.SKYPE,
      CommunicationPlatform.HANGOUTS,
      CommunicationPlatform.ZOOM,
      CommunicationPlatform.ZOOM_WEB,
    ];
  };

  static getSupported = () => {
    return [
      CommunicationPlatform.TALAERA,
      CommunicationPlatform.ZOOM,
      CommunicationPlatform.ZOOM_WEB,
    ];
  };

  static isZoom = platform => platform === CommunicationPlatform.ZOOM;
  static isTalaera = platform => platform === CommunicationPlatform.TALAERA;
}

export const RESCHEDULED_TOAST_MESSAGE =
  "Your session has been successfully rescheduled.";
export const CANCELED_TOAST_MESSAGE = "Your session has been successfully canceled.";
export const DEFAULT_BOOKED_TOAST_MESSAGE = "New session successfully booked.";

export function getSuccessComponents(message, setToastMessage) {
  setTimeout(() => {
    const messageElement = document.querySelector('[data-cy="message"]');
    if (messageElement && setToastMessage) {
      setToastMessage(null);
    }
  }, 3000);

  return (
    <div className={"talaera-success-message"} data-cy="message">
      <HStack align={"center"}>
        <div className={"iconContainer"}>
          <CheckCircleIcon style={{ color: "#27AE60" }} />
        </div>
        <span>{message}</span>
      </HStack>
    </div>
  );
}

export function getErrorComponents(respBody) {
  let errors = null;
  if (respBody && respBody.statusCode) {
    respBody = respBody.errors;
  }

  if (respBody && respBody.errorComponent) {
    errors = (
      <div className="talaera-error" data-cy="error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>{respBody.errorComponent()}</div>
        </HStack>
      </div>
    );
  } else if (respBody && respBody.errors && respBody.errors.map) {
    errors = respBody.errors.map((error, index) => (
      <div key={index} className="talaera-error" data-cy="error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>

          {error.transKey ? (
            <Trans i18nKey={error.transKey} values={error.transValues || {}}>
              {error?.description}
            </Trans>
          ) : (
            <div>{ErrorKeys[error.error_key] || error.description}</div>
          )}
        </HStack>
      </div>
    ));
  } else if (typeof respBody === "string") {
    errors = (
      <div className="talaera-error" data-cy="error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>{respBody}</div>
        </HStack>
      </div>
    );
  } else if (React.isValidElement(respBody)) {
    errors = (
      <div className="talaera-error" data-cy="error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>{respBody}</div>
        </HStack>
      </div>
    );
  } else {
    errors = (
      <div className="talaera-error" data-cy="error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>Something went wrong</div>
        </HStack>
      </div>
    );
  }

  return errors;
}

export function getCustomErrorComponent(nestedComponent) {
  return (
    <div className="talaera-error" data-cy="error">
      {nestedComponent}
    </div>
  );
}

export function getErrorsString(respBody) {
  let errors = "";
  if (respBody && respBody.statusCode) {
    respBody = respBody.errors;
  }

  if (respBody && respBody.errors) {
    respBody.errors.map(error => (errors += error.description + "; "));
    errors = errors.substring(0, errors.length - 2);
  } else if (typeof respBody === "string") {
    errors = respBody;
  } else {
    errors = "Something went wrong";
  }

  return errors;
}

export function getUserFullName(profile) {
  if (!profile) {
    return "";
  } else {
    return (
      (profile.first_name ? profile.first_name + " " : "") +
      (profile.last_name ? profile.last_name : "")
    );
  }
}

export function getUserFirstName(profile) {
  if (!profile) {
    return "";
  } else {
    return profile.first_name ? profile.first_name : "";
  }
}

export function getAddressDescription(address, type) {
  if (!address) {
    return "";
  } else {
    let description = "";
    if (type === SCHOOL_LOCATION_TYPE) {
      description =
        (address.address1 ? address.address1 + ", " : "") +
        (address.address2 ? address.address2 + ", " : "") +
        (address.zipcode ? address.zipcode : "");
    }

    description +=
      (description ? ", " : "") +
      (address.city ? address.city + ", " : "") +
      (address.state ? address.state + ", " : "") +
      (address.country ? address.country : "");
    return description;
  }
}

export function isLessonValidForJoining(scheduled) {
  // Returns true if there is less than 5 mins before scheduled
  // or a lesson is in progress (now is between scheduled and scheduled + 1h).
  return moment(getCurrentServerTime()).isBetween(
    moment(scheduled).subtract(VALID_PERIOD_OUTSIDE_LESSON, "minutes"),
    moment(scheduled).add(LESSON_DURATION, "minutes")
  );
}

export function isLessonValidForCountingDown(scheduled) {
  // Returns true if it left < 30 mins before scheduled.
  return moment(getCurrentServerTime()).isBetween(
    moment(scheduled).subtract(BEFORE_LESSON_COUNTING_DOWN_PERIOD, "minutes"),
    moment(scheduled)
  );
}

export function isLessonInPast(scheduled) {
  // Returns true if official lesson's time has already been finished.
  return moment(getCurrentServerTime()).isAfter(
    moment(scheduled).add(LESSON_DURATION, "minutes")
  );
}

export function isLessonTimeValidForPostFeedback(lesson) {
  // Returns true if there is more than 55/40 mins after scheduled time.
  const validPeriod = isAssessmentSession(lesson)
    ? VALID_FOR_POST_ASSESSMENT_FEEDBACK_PERIOD
    : VALID_FOR_POST_LESSON_FEEDBACK_PERIOD;
  return moment(getCurrentServerTime()).isAfter(
    moment(lesson.scheduled).add(validPeriod, "minutes")
  );
}

export function getCurrentServerTime() {
  const offset = localStorage.getItem(SERVER_TIME_OFFSET);

  if (!offset) {
    return moment().toDate();
  } else {
    return moment().add(offset, "milliseconds").toDate();
  }
}

export function getServerTimeOffset() {
  return localStorage.getItem(SERVER_TIME_OFFSET);
}

export function getLanguageOptionText(lang) {
  if (!lang) {
    return "";
  }

  return lang.native_name + " (" + lang.english_name + ")";
}

export function findLanguageByCode(langList, code) {
  const ret = _.findWhere(langList, { code: code });
  return ret ? ret : null;
}

export function findByCode(langList, code) {
  const ret = _.findWhere(langList, { code: code });
  return ret ? ret : null;
}

export function getSessionPrice(session, salary, invoice) {
  return getInstructorBillingType(session) === InstructorBillingType.NOT_PAID
    ? 0
    : invoice && invoice.lesson_session_salary
      ? invoice.lesson_session_salary
      : salary;
}

export function generateGUID() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
}

export function isProdOrigin() {
  return window.location.origin && window.location.origin.indexOf("app.talaera.com") > -1;
}

export class OnlineStatus {
  static OFFLINE = "offline";
  static AWAY = "away";
  static ONLINE = "online";
}

export const ONLINE_THRESHOLD = 5 * 60 * 1000; // 5 min
export const AWAY_THRESHOLD = 60 * 60 * 1000; // 1 hour

export function getStatusByLastSeen(lastSeen) {
  // Gets last_seen date and returns one of OnlineStatus.
  const diff = moment(getCurrentServerTime()).diff(moment(lastSeen));
  if (diff < ONLINE_THRESHOLD) {
    return OnlineStatus.ONLINE;
  } else if (diff >= ONLINE_THRESHOLD && diff < AWAY_THRESHOLD) {
    return OnlineStatus.AWAY;
  } else {
    return OnlineStatus.OFFLINE;
  }
}

export function getStringValue(strings, strKey) {
  if (
    localStorage.getItem("language") &&
    strings.getLanguage() !== localStorage.getItem("language")
  ) {
    strings.setLanguage(localStorage.getItem("language"));
  }

  return strings[strKey];
}

export const TERMS_OF_USE_URL = "/legal/terms-of-use.html";
export const PRIVACY_POLICY_URL = "/legal/privacy-policy.html";
export const COOKIE_POLICY_URL = "/legal/cookies.html";
export const IMPRINT_URL = "/legal/imprint.html";

export const DEFAULT_DOC_LANGUAGE = "en";

export function buildStaticDocUrl(lang, urlDetails) {
  // TODO use other languages when we translate legal docs.
  return "//talaera.com" + (lang ? "/" + lang : "") + urlDetails;
}

export function getSessionOrPlanName(session) {
  // Returns a name of the specified session, the corresponding plan's name or default name.
  if (session) {
    if (session.name) {
      return session.name;
    } else if (session.plan) {
      return session.plan.name;
    }
  }
  return "Unnamed lesson";
}

export function getContentInfoForTagPanel(content) {
  // Returns the content's tags + level info in the form as it's a tag (to show together in one TagPanel).
  const info = content.tags ? [...content.tags] : [];
  if (
    content.level &&
    (!content.type || !ExerciseTypes.isReadingExercise(content.type))
  ) {
    // Don't show level for reading exercises.
    info.unshift({
      id: content.level.id,
      name: content.level.name,
      category: TagCategory.LEVEL.name,
      isFakeTag: true,
    });
  }
  return info;
}

const TEACHER_EXERCISE_FREE_FORM_EDITOR_TOUR =
  "Talaera_teacher_exerciseFreeFormEditorTour";
const TEACHER_EXERCISE_FILL_BLANKS_EDITOR_TOUR =
  "Talaera_teacher_exerciseFillBlanksEditorTour";

export function saveFreeFormEditorTourShown() {
  localStorage.setItem(TEACHER_EXERCISE_FREE_FORM_EDITOR_TOUR, "true");
}

export function hasFreeFormEditorTourShown() {
  return localStorage.getItem(TEACHER_EXERCISE_FREE_FORM_EDITOR_TOUR) === "true";
}

export function saveFillBlanksEditorTourShown() {
  localStorage.setItem(TEACHER_EXERCISE_FILL_BLANKS_EDITOR_TOUR, "true");
}

export function hasFillBlanksEditorTourShown() {
  return localStorage.getItem(TEACHER_EXERCISE_FILL_BLANKS_EDITOR_TOUR) === "true";
}

export function copyToClipboard(textNode) {
  if (textNode) {
    let range = document.createRange();
    range.selectNodeContents(textNode);
    let selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
  }
}

export function pluralize(singular, plural, number) {
  return number === 1 ? singular : plural;
}

export const toYearDays = days => {
  const years = Math.trunc(days / 365);
  let output = "";
  if (years > 0) {
    output += years.toString() + " " + pluralize(years, "Year", "Years");
  }
  const remainingDays = days % 365;
  if (remainingDays !== 0) {
    if (output) {
      output += " ";
    }
    output += remainingDays.toString() + " " + pluralize("Day", "Days", remainingDays);
  }
  return output;
};

export const capitalize = string => string[0].toUpperCase() + string.slice(1);

export const getKeyName = ketData => {
  if (!ketData.parent) {
    // TODO Remove when we have a real LibraryRootPage.
    return "";
  }

  let key = ketData.key;
  // Cut the last '/'.
  // key = key.substring(0, key.length - 1);
  // Exclude the last folder because we likely have the same name
  // for direct parent folder and file and folder name is shown separately
  key = key.substring(0, key.lastIndexOf("/"));
  // key is like foo/bar/abc.
  const keyName = DEFAULT_ROOT_NAME + "/" + key;
  // To make it more readable.
  return keyName.split("/").join(" / ");
};

export const getTimeStringFromMinutes = minutes => {
  if (minutes) {
    const hour = Math.floor((minutes || 0) / 60);
    const min = minutes - hour * 60;
    if (hour) {
      return `${hour} hour${hour === 1 ? "" : "s"}${min > 0 ? ` ${min} min${min === 1 ? "" : "s"}` : ""}`;
    } else if (min) {
      return `${min} min${min === 1 ? "" : "s"}`;
    } else {
      return "0";
    }
  }

  return "0";
};

// TODO use structeredClone
export const cloneDeep = data => JSON.parse(JSON.stringify(data));

export const getRelativeTimeLocaleOption = time => {
  let timeObj = moment(time);
  let rangeHash = {
    minute: 60,
    hour: 12,
    day: 30,
    month: 12,
  };

  let range = Object.keys(rangeHash).find(key => {
    let duration = moment().diff(timeObj, key + "s");

    return duration / rangeHash[key] < 1;
  });

  if (!range) {
    range = "year";
  }

  return { range, time: -moment().diff(timeObj, range + "s") };
};
